import 'dayjs/locale/ko';

import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';

import { usePostUploadConferenceAbstractApi } from '@/api/conference';
import AbstractModal from '@/components/common/Modal/AbstractModal/AbstractModal';
import ModalBox from '@/components/common/Modal/ModalBox/ModalBox';
import ModalPortal from '@/components/common/Modal/ModalPortal/ModalPortal';
import DownloadIcon from '@/components/Icon/DownloadIcon';
import SingleArrowNext from '@/components/Icon/SingleArrowNext';
import { ABSTRACT_CONFERENCE_NAME } from '@/constants/menu';
import { useModalStore } from '@/store/modalStore';
import { useTokenStore } from '@/store/tokenStore';

import * as S from './style';

/** KST 기준 초록 접수 시작(이 시각부터 접수 버튼 노출) — 2026년 10월 12일(월) */
const ABSTRACT_OPEN_ISO = '2026-10-12T00:00:00+09:00';
/** KST 기준 초록 접수 마감 직후(10월 28일(수)까지 접수, 이 시각부터 등록 불가) */
const ABSTRACT_DEADLINE_ISO = '2026-10-29T00:00:00+09:00';
const ABSTRACT_RECEPTION_GUIDE = '초록 접수는 2026년 10월 12일(월) ~ 2026년 10월 28일 (수) 까지 가능합니다.';

const getKoreaDate = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));

const AbstractGuide = () => {
	dayjs.locale('ko');
	const { showModal } = useModalStore();
	const { isLogin } = useTokenStore();
	const navigate = useNavigate();
	const location = useLocation();

	const [isOpen, setIsOpen] = useState(false);
	const [presentation, setPresentation] = useState('');
	const [section, setSection] = useState('');
	const fileInputRef = useRef<HTMLInputElement>(null);

	const params = location.state?.menu ?? '학술대회 안내';
	const depthParams = location.state?.depthMenu ?? '행사정보';

	const { mutateAsync: uploadConferenceAbstract } = usePostUploadConferenceAbstractApi();

	const isReceptionOpen = () => getKoreaDate() >= new Date(ABSTRACT_OPEN_ISO);
	const isDeadlinePassed = () => getKoreaDate() >= new Date(ABSTRACT_DEADLINE_ISO);

	const handleAbstractRegister = async () => {
		if (!isReceptionOpen()) {
			showModal(ABSTRACT_RECEPTION_GUIDE, 'confirm');
			return;
		}
		if (isDeadlinePassed()) {
			showModal('초록 등록 기한이 마감되었습니다.', 'confirm');
			return;
		}

		if (!isLogin()) {
			showModal('로그인이 필요합니다.', 'login', '로그인 하러가기', () => navigate('/auth/login'));
			return;
		}

		setIsOpen(true);
	};

	const getAbstractInfo = (presentationValue: string, sectionValue: string) => {
		setPresentation(presentationValue);
		setSection(sectionValue);

		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			showModal('파일을 선택해주세요.', 'confirm');
			return;
		}

		showModal(`[${presentation}][${section}]\n해당 파일로 등록하시겠습니까?`, 'register', '', () => uploadFile(file));
	};

	const uploadFile = async (file: File) => {
		try {
			const formData = new FormData();
			formData.append('abstract_file', file);
			formData.append('conference_name', ABSTRACT_CONFERENCE_NAME);
			formData.append('abstract_presentation', presentation);
			formData.append('abstract_division', section);
			const response = await uploadConferenceAbstract(formData);

			if (response.data?.success) {
				showModal('등록이 완료되었습니다.', 'success');
			} else {
				showModal('초록 등록에 실패했습니다. 다시 시도해주세요.', 'error');
			}
		} catch (error) {
			console.error('파일 업로드 에러:', error);
			showModal('초록 등록 중 오류가 발생했습니다. 다시 시도해주세요.', 'error');
		}
	};

	const handleAbstractConfirm = async () => {
		if (!isLogin()) {
			showModal('로그인이 필요합니다.', 'login', '로그인 하러가기', () => navigate('/auth/login'));
			return;
		}
		navigate('/society', { state: { step: '초록 확인', menu: params, depthMenu: depthParams } });
	};

	return (
		<S.Container>
			<S.ContentBox>
				<S.ContentTitle>기본정보</S.ContentTitle>
				<S.ContentGuide>
					초록 양식을 다운받으신 후 초록 업로드 부탁드립니다.
					<br />
					(국문 또는 영문 1가지만 업로드)
				</S.ContentGuide>
				<S.BasicInfoBox>
					{!isReceptionOpen() && (
						<S.HighlightBox>
							<span>{ABSTRACT_RECEPTION_GUIDE}</span>
						</S.HighlightBox>
					)}
					{isReceptionOpen() && !isDeadlinePassed() && (
						<S.HighlightBox>
							<span>초록제출기간:</span>
							<span className="point">
								{dayjs(ABSTRACT_OPEN_ISO).format('YYYY년 M월 D일 (dd)')} ~ {dayjs(ABSTRACT_DEADLINE_ISO).subtract(1, 'day').format('YYYY년 M월 D일 (dd)')} 까지
							</span>
						</S.HighlightBox>
					)}
					{isReceptionOpen() && isDeadlinePassed() && (
						<S.HighlightBox>
							<span>초록 접수 기간이 종료되었습니다.</span>
						</S.HighlightBox>
					)}
					<S.BasicInfoButtonBox>
						<input
							type="file"
							ref={fileInputRef}
							style={{ display: 'none' }}
							accept=".docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
							onClick={(e) => {
								(e.target as HTMLInputElement).value = '';
							}}
							onChange={handleFileChange}
						/>
						{isReceptionOpen() && !isDeadlinePassed() && (
							<S.BlueButton onClick={handleAbstractRegister}>
								초록 등록
								<SingleArrowNext iconColor="#FFFFFF" width="20" height="20" />
							</S.BlueButton>
						)}
						<S.WhiteButton type="button" onClick={handleAbstractConfirm}>
							초록 확인
							<SingleArrowNext iconColor="#01337d" width="20" height="20" />
						</S.WhiteButton>
					</S.BasicInfoButtonBox>
					{/* <p>
						※ 초록 수정이 필요할 경우 <strong>scsk1968@naver.com</strong>로 문의주세요.
					</p> */}
				</S.BasicInfoBox>
				{/* <S.HighlightBox>
					<span>초록제출기간:</span>
					<span className="point">2025년 4월 25일 – 2025년 5월 30일</span>
				</S.HighlightBox> */}
				<S.BasicInfo>
					<li>1저자가 초록을 등록해야 합니다.</li>
					<li>요약 분량은 A4용지 0.5~1장이 되도록 합니다.</li>
					<li>임상실험을 실시한 경우 IRB승인번호를 필수 기재합니다.</li>
					<li>Keywords는 3~5 영문 단어로 작성합니다.</li>
					<li>초록등록 업로드 시 Word파일로 업로드 부탁드립니다(PDF파일 X)</li>
				</S.BasicInfo>
			</S.ContentBox>
			<S.ContentBox>
				<S.ContentTitle>초록 양식 다운로드</S.ContentTitle>
				<S.BasicInfoButtonBox>
					<S.BlackButton href="/docsFile/abstract_form_kr.docx" download>
						<DownloadIcon />
						국문
					</S.BlackButton>
					<S.BlackButton href="/docsFile/abstract_form_en.docx" download>
						<DownloadIcon />
						영문
					</S.BlackButton>
				</S.BasicInfoButtonBox>
			</S.ContentBox>
			{isOpen && (
				<ModalPortal>
					<ModalBox onClick={() => setIsOpen(false)}>
						<AbstractModal
							closeModal={setIsOpen}
							onComplete={({ presentation, section }) => {
								getAbstractInfo(presentation, section);
							}}
						/>
					</ModalBox>
				</ModalPortal>
			)}
		</S.Container>
	);
};
export default AbstractGuide;
